// Acciones del chofer. Cada una se guarda primero en la cola local (nada se pierde sin señal) y despierta al
// sincronizador. La pantalla se actualiza sola porque la vista se arma con la cola (vista.ts).
import { hoyISO, type MotivoNoEntrega, type TipoComprobante, type TipoOperacion, uuidv7 } from '@reparto/shared/nucleo';
import { db, type OperacionLocal } from './db';
import { despertar } from './sync';
import type { MedioCobro } from '@reparto/shared/nucleo';

async function encolar(
  tipo: TipoOperacion,
  payload: Record<string, unknown>,
  extra: Partial<OperacionLocal> = {},
): Promise<OperacionLocal> {
  const ahora = new Date().toISOString();
  const op: OperacionLocal = {
    operacion_id: uuidv7(),
    tipo,
    payload,
    hora_dispositivo: ahora,
    estado: 'pendiente',
    intentos: 0,
    creado_en: ahora,
    ...extra,
  };
  await db.cola.add(op);
  despertar();
  return op;
}

export const tildar = (movilId: string, fecha: string, pedidoId: string, cargado: boolean) =>
  encolar('tildar', { movil_id: movilId, fecha, pedido_id: pedidoId, cargado });

/** Carga en parte (decisión D54): cuántos bultos de un artículo suben, con el motivo si es menos de lo pedido. */
export const cargarItem = (
  movilId: string,
  fecha: string,
  pedidoItemId: string,
  bultos: number,
  motivo?: string | null,
) =>
  encolar('cargar_item', {
    movil_id: movilId,
    fecha,
    pedido_item_id: pedidoItemId,
    bultos,
    ...(motivo && { motivo }),
    tildar_pedido: true,
  });

/** Confirma la carga proponiendo los ids de la ruta y de cada parada (así se puede seguir sin señal). */
export function confirmarCarga(
  movilId: string,
  fecha: string,
  tildados: string[],
  noCargados: Array<{ pedido_id: string; motivo: string }>,
) {
  const rutaId = uuidv7();
  return encolar('confirmar_carga', {
    movil_id: movilId,
    fecha: fecha || hoyISO(),
    ruta_id: rutaId,
    paradas: tildados.map((pedidoId) => ({ pedido_id: pedidoId, parada_id: uuidv7() })),
    no_cargados: noCargados,
  });
}

/**
 * "Recibí conforme" con la firma (decisión D54), cuando la carga también la controla el encargado: va con la huella de
 * la carga que vio el chofer (si cambió, el servidor la rechaza y hay que revisarla). No propone ruta: la carga la
 * cierra el servidor cuando están las dos conformidades, y recién ahí se sale.
 */
export const recibirConforme = (
  movilId: string,
  fecha: string,
  d: {
    huella: string;
    noCargados: Array<{ pedido_id: string; motivo: string }>;
    firma: string | null;
    observacion?: string;
  },
) =>
  encolar(
    'confirmar_carga',
    {
      movil_id: movilId,
      fecha: fecha || hoyISO(),
      huella: d.huella,
      no_cargados: d.noCargados,
      archivo_ids: [],
      ...(d.observacion?.trim() && { observacion: d.observacion.trim() }),
    },
    { archivos_locales: d.firma ? [d.firma] : [] },
  );

export const optimizar = (rutaId: string) => encolar('optimizar', { ruta_id: rutaId });
export const reordenar = (rutaId: string, paradaIds: string[]) =>
  encolar('reordenar', { ruta_id: rutaId, parada_ids: paradaIds });
export const salir = (rutaId: string) => encolar('salir', { ruta_id: rutaId });

export interface Posicion {
  lat: number;
  lng: number;
  precision_m?: number;
}

export const llegada = (paradaId: string, posicion?: Posicion) =>
  encolar('llegada', { parada_id: paradaId, ...(posicion && { posicion }) });

export interface DatosEntrega {
  items: Array<{ pedido_item_id: string; bultos_entregados: number }>;
  cobro: { medio: MedioCobro; monto_cobrado: number; referencia?: string | null };
  comprobante: TipoComprobante | null;
  archivosLocales: string[];
  observaciones?: string | null;
  posicion?: Posicion;
  /** El documento "al entregar" o "al cobrar" que armó la app (decisión D57). */
  documento?: { comprobante_id: string; tipo: string; reserva_id: string | null; numero: number | null };
}

export const entregar = (paradaId: string, d: DatosEntrega) =>
  encolar(
    'entregar',
    {
      parada_id: paradaId,
      items: d.items,
      cobro: {
        medio: d.cobro.medio,
        monto_cobrado: d.cobro.monto_cobrado,
        ...(d.cobro.referencia && { referencia: d.cobro.referencia }),
      },
      comprobante: d.comprobante,
      archivo_ids: [],
      ...(d.observaciones && { observaciones: d.observaciones }),
      ...(d.posicion && { posicion: d.posicion }),
      ...(d.documento && { documento: d.documento }),
    },
    { archivos_locales: d.archivosLocales },
  );

export const noEntregar = (
  paradaId: string,
  motivo: Exclude<MotivoNoEntrega, 'sin_visitar'>,
  texto?: string,
  posicion?: Posicion,
) =>
  encolar('no_entregar', {
    parada_id: paradaId,
    motivo,
    ...(texto && { motivo_texto: texto }),
    ...(posicion && { posicion }),
  });

export const cerrar = (rutaId: string, observacion?: string) =>
  encolar('cerrar', { ruta_id: rutaId, ...(observacion && { observacion_chofer: observacion }) });
